/**
 * Room Care & Maintenance Page
 * Allows guests to request housekeeping items (pillows, blankets, amenities) and report repairs.
 */

import { t, getCurrentLanguage } from '../i18n';
import { Header, renderServiceTabs, LoadingSpinner, AppVersionFooter, BackToTopButton, initBackToTop } from '../components';
import { getState, navigateTo } from '../store';
import {
  fetchGuestServicesConfig,
  submitGuestRequest,
  fetchGuestRequests,
  cancelGuestRequest,
  type GuestServiceConfigResponse,
} from '../api';
import {
  DEFAULT_HOUSEKEEPING_ITEMS,
  DEFAULT_MAINTENANCE_CATEGORIES,
} from '@revio/shared';

let activeTab: 'housekeeping' | 'maintenance' | 'my-requests' = 'housekeeping';
let serviceConfig: GuestServiceConfigResponse = {
  housekeepingItems: DEFAULT_HOUSEKEEPING_ITEMS,
  maintenanceCategories: DEFAULT_MAINTENANCE_CATEGORIES,
};
let isConfigLoaded = false;
let selectedItems: Record<string, number> = {}; // { itemId: quantity }
let selectedIssueCategory = '';
let myRequestsList: any[] = [];
let isLoading = false;
let isSubmitting = false;
let successMessage = '';

// Form state variables preserved across re-renders
let formRoomNumber = '';
let formGuestName = '';
let formGuestPhone = '';
let formNotes = '';
let formIssueDesc = '';

export async function loadRoomServiceConfigAsync(): Promise<void> {
  if (isConfigLoaded) return;
  try {
    const cfg = await fetchGuestServicesConfig();
    if (cfg && (Array.isArray(cfg.housekeepingItems) || Array.isArray(cfg.maintenanceCategories) || cfg.coverImage !== undefined)) {
      serviceConfig = {
        housekeepingItems: Array.isArray(cfg.housekeepingItems) ? cfg.housekeepingItems : DEFAULT_HOUSEKEEPING_ITEMS,
        maintenanceCategories: Array.isArray(cfg.maintenanceCategories) ? cfg.maintenanceCategories : DEFAULT_MAINTENANCE_CATEGORIES,
        coverImage: typeof cfg.coverImage === 'string' ? cfg.coverImage : '',
      };
      isConfigLoaded = true;
      (window as any).renderApp?.();
    }
  } catch (e) {
    console.warn('[RoomService] Failed to load config async:', e);
  }
}


export function setRoomServiceActiveTab(tab: 'housekeeping' | 'maintenance' | 'my-requests'): void {
  activeTab = tab;
}

export function renderRoomServiceHeroCard(coverImage?: string, savedRoomNumber?: string): string {
  return `
    <div id="room-service-hero-card" style="background:#fff;border-radius:14px;overflow:hidden;margin-bottom:16px;box-shadow:0 1px 3px rgba(0,0,0,0.08);padding:0;">
      <div style="padding:8px 8px 0 8px;">
        ${coverImage ? `
          <div style="position:relative;border-radius:10px;overflow:hidden;height:180px;background:#f1f5f9;margin:0;">
            <img
              src="${coverImage}"
              alt="${t('roomServiceTitle')}"
              style="width:100%;height:100%;object-fit:cover;display:block;"
              onerror="this.style.display='none';this.nextElementSibling.style.display='flex';"
            />
            <div style="display:none;position:absolute;inset:0;background:linear-gradient(135deg, #10b981 0%, #059669 100%);flex-direction:column;align-items:center;justify-content:center;color:#fff;padding:16px;text-align:center;">
              <div style="font-size:38px;margin-bottom:6px;">🛎️</div>
              <div style="font-size:18px;font-weight:700;color:#fff;margin-bottom:4px;">${t('roomServiceTitle')}</div>
              <div style="font-size:13px;color:rgba(255,255,255,0.9);max-width:360px;line-height:1.4;">${t('roomServiceSubtitle')}</div>
            </div>
          </div>
        ` : `
          <div style="position:relative;border-radius:10px;overflow:hidden;height:180px;background:linear-gradient(135deg, #10b981 0%, #059669 100%);display:flex;flex-direction:column;align-items:center;justify-content:center;color:#fff;padding:16px;text-align:center;margin:0;">
            <div style="font-size:38px;margin-bottom:6px;">🛎️</div>
            <div style="font-size:18px;font-weight:700;color:#fff;margin-bottom:4px;">${t('roomServiceTitle')}</div>
            <div style="font-size:13px;color:rgba(255,255,255,0.9);max-width:360px;line-height:1.4;">${t('roomServiceSubtitle')}</div>
          </div>
        `}
      </div>
      <div style="padding:12px 14px;display:flex;align-items:center;justify-content:space-between;background:#fafafa;border-top:1px solid #f0f0f0;">
        <span style="display:inline-flex;align-items:center;gap:6px;font-size:12px;color:#166534;font-weight:600;">
          <span style="width:8px;height:8px;border-radius:50%;background:#10b981;display:inline-block;"></span> 24/7 Available
        </span>
        <span style="font-size:12px;color:#64748b;">
          ${savedRoomNumber ? `📍 ${t('roomPrefix')} ${savedRoomNumber}` : `📍 ${t('tabHousekeeping')} & ${t('tabMaintenance')}`}
        </span>
      </div>
    </div>
  `;
}

export function renderRoomServicePage(): string {
  const lang = getCurrentLanguage();
  const state = getState();
  const guestProfile = state.user;

  // Support tab & room resolution from URL
  try {
    const params = new URLSearchParams(window.location.search || '');
    const tabParam = (params.get('tab') || '').toLowerCase().replace(/[\s_-]/g, '');
    if (tabParam === 'engineer' || tabParam === 'maintenance' || tabParam === 'repair' || tabParam === 'maint') {
      activeTab = 'maintenance';
    } else if (tabParam === 'myrequest' || tabParam === 'myrequests' || tabParam === 'requests' || tabParam === 'history') {
      activeTab = 'my-requests';
    } else if (tabParam === 'hk' || tabParam === 'housekeeping') {
      activeTab = 'housekeeping';
    }

    const roomParam = (params.get('room') || params.get('roomNumber') || '').trim();
    if (roomParam) {
      formRoomNumber = roomParam;
      try { localStorage.setItem('_guest_room_number', roomParam); } catch (_) {}
    }
  } catch (_) {}

  // Auto-populate saved room number or phone from localStorage if available
  const savedRoomNumber = formRoomNumber || localStorage.getItem('_guest_room_number') || '';
  const savedGuestName = formGuestName || localStorage.getItem('_guest_name') || guestProfile?.displayName || '';
  const savedGuestPhone = formGuestPhone || localStorage.getItem('_guest_phone') || '';

  return `
    ${Header(t('roomServiceTitle'), false, true)}
    ${renderServiceTabs('room-service', 'room_service')}

    <div class="page-content room-service-page" style="padding: 16px; padding-bottom: 100px;">
      <!-- Service Pill Tab (matching Rooms, Spa, Restaurant & MICE) -->
      <div id="room-service-pill-container" style="display:flex;gap:8px;overflow-x:auto;-webkit-overflow-scrolling:touch;padding-bottom:8px;margin-bottom:12px;scrollbar-width:none;-ms-overflow-style:none;">
        <button
          type="button"
          style="flex:0 0 auto;padding:8px 16px;border-radius:20px;border:1.5px solid #00B900;background:#00B900;color:#fff;font-size:13px;font-weight:600;white-space:nowrap;cursor:default;"
        >
          🛎️ ${t('roomServiceTitle')}${savedRoomNumber ? ` · ${t('roomPrefix')} ${savedRoomNumber}` : ''}
        </button>
      </div>

      <!-- Unified Hero Card (matching Spa, Restaurant & MICE visual rhythm) -->
      ${renderRoomServiceHeroCard(serviceConfig.coverImage, savedRoomNumber)}

      <!-- Tab Navigation (Segmented Style matching 16px page grid) -->
      <div style="display: flex; gap: 8px; margin-bottom: 16px;">
        <button
          id="tab-btn-hk"
          class="service-subtab ${activeTab === 'housekeeping' ? 'active' : ''}"
          style="flex: 1; padding: 12px 4px; font-size: 13px; font-weight: 600; border-radius: 10px; border: 1.5px solid ${activeTab === 'housekeeping' ? '#00B900' : '#e2e8f0'}; background: ${activeTab === 'housekeeping' ? '#00B900' : '#fff'}; color: ${activeTab === 'housekeeping' ? '#fff' : '#475569'}; cursor: pointer; transition: all 0.15s ease; text-align: center; box-shadow: ${activeTab === 'housekeeping' ? '0 2px 6px rgba(0,185,0,0.2)' : 'none'};"
          onclick="window.switchRoomServiceTab?.('housekeeping')"
        >
          🧹 ${t('tabHousekeeping')}
        </button>
        <button
          id="tab-btn-maint"
          class="service-subtab ${activeTab === 'maintenance' ? 'active' : ''}"
          style="flex: 1; padding: 12px 4px; font-size: 13px; font-weight: 600; border-radius: 10px; border: 1.5px solid ${activeTab === 'maintenance' ? '#2563eb' : '#e2e8f0'}; background: ${activeTab === 'maintenance' ? '#2563eb' : '#fff'}; color: ${activeTab === 'maintenance' ? '#fff' : '#475569'}; cursor: pointer; transition: all 0.15s ease; text-align: center; box-shadow: ${activeTab === 'maintenance' ? '0 2px 6px rgba(37,99,235,0.2)' : 'none'};"
          onclick="window.switchRoomServiceTab?.('maintenance')"
        >
          🛠️ ${t('tabMaintenance')}
        </button>
        <button
          id="tab-btn-history"
          class="service-subtab ${activeTab === 'my-requests' ? 'active' : ''}"
          style="flex: 1; padding: 12px 4px; font-size: 13px; font-weight: 600; border-radius: 10px; border: 1.5px solid ${activeTab === 'my-requests' ? '#7c3aed' : '#e2e8f0'}; background: ${activeTab === 'my-requests' ? '#7c3aed' : '#fff'}; color: ${activeTab === 'my-requests' ? '#fff' : '#475569'}; cursor: pointer; transition: all 0.15s ease; text-align: center; box-shadow: ${activeTab === 'my-requests' ? '0 2px 6px rgba(124,58,237,0.2)' : 'none'};"
          onclick="window.switchRoomServiceTab?.('my-requests')"
        >
          📋 ${t('tabMyRequests')}
        </button>
      </div>

      <!-- Content Area -->
      <div id="room-service-content">
        ${renderCurrentTabContent(lang, savedRoomNumber, savedGuestName, savedGuestPhone)}
      </div>

      ${AppVersionFooter()}
    </div>

    ${BackToTopButton('roomservice')}
  `;
}

function renderCurrentTabContent(lang: string, roomNo: string, guestName: string, guestPhone: string): string {
  if (activeTab === 'housekeeping') {
    return renderHousekeepingTab(lang, roomNo, guestName, guestPhone);
  } else if (activeTab === 'maintenance') {
    return renderMaintenanceTab(lang, roomNo, guestName, guestPhone);
  } else {
    return renderMyRequestsTab(lang, roomNo);
  }
}

function renderHousekeepingTab(lang: string, roomNo: string, guestName: string, guestPhone: string): string {
  const items = serviceConfig?.housekeepingItems || [];
  const currentRoom = formRoomNumber || roomNo;
  const currentName = formGuestName || guestName;
  const currentPhone = formGuestPhone || guestPhone;

  return `
    <div style="background: #fff; border-radius: 16px; padding: 16px 12px; box-shadow: 0 1px 3px rgba(0,0,0,0.06); border: 1px solid #f1f5f9; margin-bottom: 16px; box-sizing: border-box; overflow: hidden;">
      <!-- Room Number & Contact Section -->
      <div style="background: #f8fafc; padding: 14px; border-radius: 12px; border: 1px solid #e2e8f0; margin-bottom: 18px;">
        <label style="display: block; font-size: 12px; font-weight: 700; color: #1e293b; margin-bottom: 6px;">
          ${t('roomNumberLabel')} <span style="color: #dc2626;">*</span>
        </label>
        <input
          type="text"
          id="rs-room-number"
          value="${currentRoom}"
          placeholder="${t('roomNumberPlaceholder')}"
          oninput="window.handleRoomServiceInput?.('room', this.value)"
          style="width: 100%; height: 42px; border: 1px solid #cbd5e1; border-radius: 8px; padding: 0 12px; font-size: 15px; font-weight: 600; color: #0f172a; margin-bottom: 10px; background: #fff;"
        />

        <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 8px;">
          <div>
            <label style="display: block; font-size: 11px; font-weight: 600; color: #64748b; margin-bottom: 4px;">
              ${t('guestNameLabel')}
            </label>
            <input
              type="text"
              id="rs-guest-name"
              value="${currentName}"
              placeholder="Name"
              oninput="window.handleRoomServiceInput?.('name', this.value)"
              style="width: 100%; height: 36px; border: 1px solid #cbd5e1; border-radius: 8px; padding: 0 10px; font-size: 13px; background: #fff;"
            />
          </div>
          <div>
            <label style="display: block; font-size: 11px; font-weight: 600; color: #64748b; margin-bottom: 4px;">
              ${t('guestPhoneLabel')}
            </label>
            <input
              type="tel"
              id="rs-guest-phone"
              value="${currentPhone}"
              placeholder="081-xxx-xxxx"
              oninput="window.handleRoomServiceInput?.('phone', this.value)"
              style="width: 100%; height: 36px; border: 1px solid #cbd5e1; border-radius: 8px; padding: 0 10px; font-size: 13px; background: #fff;"
            />
          </div>
        </div>
      </div>

      <!-- Items Grid Selection -->
      <div style="margin-bottom: 18px;">
        <div style="display: flex; flex-direction: column; gap: 2px; margin-bottom: 10px;">
          <div style="display: flex; justify-content: space-between; align-items: baseline; flex-wrap: wrap; gap: 4px;">
            <label style="font-size: 13px; font-weight: 700; color: #1e293b;">
              ${t('selectItemsTitle')}
            </label>
            <span style="font-size: 11px; color: #64748b;">${t('tapQtyHint')}</span>
          </div>
        </div>

        <div style="display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 8px; box-sizing: border-box;">
          ${items
            .filter((item) => item.isActive !== false)
            .map((item) => {
              const itemName = (item.name as any)[lang] || item.name.en || item.name.th;
              const maxQty = typeof item.maxQuantity === 'number' && item.maxQuantity > 0 ? item.maxQuantity : 4;
              const count = selectedItems[item.id] || 0;
              const isSelected = count > 0;
              const isMaxReached = count >= maxQty;
              const limitHintText = t('maxItemLimit').replace('{max}', String(maxQty));

              return `
                <div style="border: 1.5px solid ${isSelected ? '#00B900' : '#e2e8f0'}; background: ${isSelected ? '#f0fdf4' : '#fff'}; border-radius: 12px; padding: 10px 8px; display: flex; flex-direction: column; justify-content: space-between; transition: all 0.15s; min-height: 106px; box-sizing: border-box; min-width: 0; overflow: hidden;">
                  <div style="display: flex; flex-direction: column; gap: 4px; margin-bottom: 8px; min-width: 0;">
                    <div style="display: flex; align-items: flex-start; gap: 6px; min-width: 0;">
                      <span style="font-size: 20px; flex-shrink: 0; line-height: 1.2;">${item.icon || '🧹'}</span>
                      <span style="font-size: 12px; font-weight: 600; color: ${isSelected ? '#15803d' : '#1e293b'}; line-height: 1.3; word-break: break-word; overflow-wrap: break-word; -webkit-hyphens: auto; hyphens: auto; min-width: 0;">
                        ${itemName}
                      </span>
                    </div>
                    <span style="font-size: 10px; color: ${isMaxReached ? '#ea580c' : '#94a3b8'}; font-weight: 500; word-break: break-word; overflow-wrap: break-word; min-width: 0; padding-left: 2px;">
                      ${limitHintText}
                    </span>
                  </div>

                  <div style="display: flex; align-items: center; justify-content: flex-end; gap: 6px; margin-top: auto; flex-shrink: 0;">
                    <button
                      type="button"
                      style="width: 26px; height: 26px; border-radius: 6px; border: 1px solid #cbd5e1; background: #fff; font-weight: bold; font-size: 15px; display: flex; align-items: center; justify-content: center; color: #475569; cursor: pointer; flex-shrink: 0;"
                      onclick="window.adjustItemQty?.('${item.id}', -1)"
                    >
                      -
                    </button>
                    <span style="font-size: 13px; font-weight: 700; min-width: 16px; text-align: center; color: ${isSelected ? '#00B900' : '#64748b'};">
                      ${count}
                    </span>
                    <button
                      type="button"
                      ${isMaxReached ? 'disabled' : ''}
                      style="width: 26px; height: 26px; border-radius: 6px; border: none; background: ${isMaxReached ? '#cbd5e1' : '#00B900'}; font-weight: bold; font-size: 15px; display: flex; align-items: center; justify-content: center; color: #fff; cursor: ${isMaxReached ? 'not-allowed' : 'pointer'}; opacity: ${isMaxReached ? '0.7' : '1'}; flex-shrink: 0;"
                      onclick="window.adjustItemQty?.('${item.id}', 1)"
                    >
                      +
                    </button>
                  </div>
                </div>
              `;
            })
            .join('')}
        </div>
      </div>

      <!-- Additional Notes Textarea -->
      <div style="margin-bottom: 20px;">
        <label style="display: block; font-size: 12px; font-weight: 600; color: #475569; margin-bottom: 6px;">
          ${t('customNotesLabel')}
        </label>
        <textarea
          id="rs-notes"
          rows="2"
          placeholder="${t('customNotesPlaceholder')}"
          oninput="window.handleRoomServiceInput?.('notes', this.value)"
          style="width: 100%; border: 1px solid #cbd5e1; border-radius: 8px; padding: 10px 12px; font-size: 14px; font-family: inherit; resize: vertical; background: #fff; pointer-events: auto !important; -webkit-user-select: text !important; user-select: text !important; touch-action: manipulation; box-sizing: border-box;"
        >${formNotes}</textarea>
      </div>

      <!-- Submit Button -->
      <button
        type="button"
        id="rs-submit-btn"
        style="width: 100%; height: 48px; border-radius: 12px; border: none; background: #00B900; color: #fff; font-size: 15px; font-weight: 700; cursor: pointer; display: flex; align-items: center; justify-content: center; gap: 8px; box-shadow: 0 4px 12px rgba(0,185,0,0.3); transition: all 0.2s;"
        onclick="window.handleSubmitHousekeepingRequest?.()"
      >
        <span>🧹</span> ${t('submitHousekeepingBtn')}
      </button>
    </div>
  `;
}

function renderMaintenanceTab(lang: string, roomNo: string, guestName: string, guestPhone: string): string {
  const categories = serviceConfig?.maintenanceCategories || [];
  const currentRoom = formRoomNumber || roomNo;
  const currentName = formGuestName || guestName;
  const currentPhone = formGuestPhone || guestPhone;

  return `
    <div style="background: #fff; border-radius: 16px; padding: 16px 12px; box-shadow: 0 1px 3px rgba(0,0,0,0.06); border: 1px solid #f1f5f9; margin-bottom: 16px; box-sizing: border-box; overflow: hidden;">
      <!-- Room Number & Contact Section -->
      <div style="background: #f8fafc; padding: 14px; border-radius: 12px; border: 1px solid #e2e8f0; margin-bottom: 18px;">
        <label style="display: block; font-size: 12px; font-weight: 700; color: #1e293b; margin-bottom: 6px;">
          ${t('roomNumberLabel')} <span style="color: #dc2626;">*</span>
        </label>
        <input
          type="text"
          id="rs-room-number-maint"
          value="${currentRoom}"
          placeholder="${t('roomNumberPlaceholder')}"
          oninput="window.handleRoomServiceInput?.('room', this.value)"
          style="width: 100%; height: 42px; border: 1px solid #cbd5e1; border-radius: 8px; padding: 0 12px; font-size: 15px; font-weight: 600; color: #0f172a; margin-bottom: 10px; background: #fff;"
        />

        <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 8px;">
          <div>
            <label style="display: block; font-size: 11px; font-weight: 600; color: #64748b; margin-bottom: 4px;">
              ${t('guestNameLabel')}
            </label>
            <input
              type="text"
              id="rs-guest-name-maint"
              value="${currentName}"
              placeholder="Name"
              oninput="window.handleRoomServiceInput?.('name', this.value)"
              style="width: 100%; height: 36px; border: 1px solid #cbd5e1; border-radius: 8px; padding: 0 10px; font-size: 13px; background: #fff;"
            />
          </div>
          <div>
            <label style="display: block; font-size: 11px; font-weight: 600; color: #64748b; margin-bottom: 4px;">
              ${t('guestPhoneLabel')}
            </label>
            <input
              type="tel"
              id="rs-guest-phone-maint"
              value="${currentPhone}"
              placeholder="081-xxx-xxxx"
              oninput="window.handleRoomServiceInput?.('phone', this.value)"
              style="width: 100%; height: 36px; border: 1px solid #cbd5e1; border-radius: 8px; padding: 0 10px; font-size: 13px; background: #fff;"
            />
          </div>
        </div>
      </div>

      <!-- Categories Selection -->
      <div style="margin-bottom: 18px;">
        <label style="display: block; font-size: 13px; font-weight: 700; color: #1e293b; margin-bottom: 10px;">
          ${t('selectIssueTitle')}
        </label>
        <div style="display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 8px; box-sizing: border-box;">
          ${categories
            .filter((cat) => cat.isActive !== false)
            .map((cat) => {
              const catName = (cat.name as any)[lang] || cat.name.en || cat.name.th;
              const isSelected = selectedIssueCategory === cat.id;

              return `
                <div
                  style="border: 1.5px solid ${isSelected ? '#2563eb' : '#e2e8f0'}; background: ${isSelected ? '#eff6ff' : '#fff'}; border-radius: 12px; padding: 10px 8px; cursor: pointer; transition: all 0.15s; display: flex; align-items: flex-start; gap: 6px; box-sizing: border-box; min-width: 0; overflow: hidden;"
                  onclick="window.selectIssueCategory?.('${cat.id}')"
                >
                  <span style="font-size: 20px; flex-shrink: 0; line-height: 1.2;">${cat.icon || '🔧'}</span>
                  <span style="font-size: 12px; font-weight: 600; color: ${isSelected ? '#1d4ed8' : '#1e293b'}; line-height: 1.3; word-break: break-word; overflow-wrap: break-word; -webkit-hyphens: auto; hyphens: auto; min-width: 0;">
                    ${catName}
                  </span>
                </div>
              `;
            })
            .join('')}
        </div>
      </div>

      <!-- Problem Description -->
      <div style="margin-bottom: 20px;">
        <label style="display: block; font-size: 12px; font-weight: 600; color: #475569; margin-bottom: 6px;">
          ${t('issueDescriptionLabel')} <span style="color: #dc2626;">*</span>
        </label>
        <textarea
          id="rs-issue-desc"
          rows="3"
          placeholder="${t('issueDescriptionPlaceholder')}"
          oninput="window.handleRoomServiceInput?.('issue', this.value)"
          style="width: 100%; border: 1px solid #cbd5e1; border-radius: 8px; padding: 10px 12px; font-size: 14px; font-family: inherit; resize: vertical; background: #fff; pointer-events: auto !important; -webkit-user-select: text !important; user-select: text !important; touch-action: manipulation; box-sizing: border-box;"
        >${formIssueDesc}</textarea>
      </div>

      <!-- Submit Button -->
      <button
        type="button"
        id="rs-submit-maint-btn"
        style="width: 100%; height: 48px; border-radius: 12px; border: none; background: #2563eb; color: #fff; font-size: 15px; font-weight: 700; cursor: pointer; display: flex; align-items: center; justify-content: center; gap: 8px; box-shadow: 0 4px 12px rgba(37,99,235,0.3); transition: all 0.2s;"
        onclick="window.handleSubmitMaintenanceRequest?.()"
      >
        <span>🛠️</span> ${t('submitMaintenanceBtn')}
      </button>
    </div>
  `;
}

function renderMyRequestsTab(lang: string, roomNo: string): string {
  if (myRequestsList.length === 0) {
    return `
      <div style="background: #fff; border-radius: 16px; padding: 36px 20px; text-align: center; border: 1px dashed #cbd5e1; margin-bottom: 16px;">
        <div style="font-size: 40px; margin-bottom: 8px;">📭</div>
        <h3 style="font-size: 14px; font-weight: 700; color: #475569; margin: 0 0 6px 0;">${t('noActiveRequests')}</h3>
        <p style="font-size: 12px; color: #94a3b8; margin: 0 0 16px 0;">${roomNo ? `${t('roomPrefix')} ${roomNo}` : ''}</p>
        <button
          type="button"
          style="padding: 8px 18px; border-radius: 8px; border: 1px solid #00B900; background: #f0fdf4; color: #00B900; font-size: 13px; font-weight: 600; cursor: pointer;"
          onclick="window.switchRoomServiceTab?.('housekeeping')"
        >
          ${t('newRequestBtn')}
        </button>
      </div>
    `;
  }

  return `
    <div style="display: flex; flex-direction: column; gap: 12px; margin-bottom: 16px;">
      <div style="display: flex; justify-content: space-between; align-items: center; padding: 0 4px;">
        <span style="font-size: 13px; font-weight: 700; color: #334155;">${t('latestRequestsHeader')}</span>
        <button
          style="font-size: 12px; color: #2563eb; background: none; border: none; cursor: pointer; font-weight: 600;"
          onclick="window.refreshMyRequests?.()"
        >
          ${t('refreshStatusBtn')}
        </button>
      </div>

      ${myRequestsList
        .map((req) => {
          const isDone = req.status === 'completed';
          const isInProgress = req.status === 'in_progress';

          const statusColor = isDone
            ? '#16a34a'
            : isInProgress
            ? '#2563eb'
            : '#d97706';

          const statusBg = isDone
            ? '#dcfce7'
            : isInProgress
            ? '#dbeafe'
            : '#fef3c7';

          const statusText = isDone
            ? t('statusReqCompleted')
            : isInProgress
            ? t('statusReqInProgress')
            : t('statusReqPending');

          const icon = req.category === 'maintenance' ? '🛠️' : '🧹';

          const roomLabel = req.roomNumber ? `${t('roomPrefix')} ${req.roomNumber}` : (req.location || '');

          let assignedLabel = req.assignedTo || t('waitingStaffAccept');
          if (assignedLabel.includes('แม่บ้าน') || assignedLabel.toLowerCase().includes('housekeeping')) {
            assignedLabel = t('teamHousekeeping');
          } else if (assignedLabel.includes('ช่าง') || assignedLabel.toLowerCase().includes('maintenance')) {
            assignedLabel = t('teamMaintenance');
          }

          return `
            <div style="background: #fff; border-radius: 14px; padding: 14px 16px; border: 1px solid #e2e8f0; box-shadow: 0 1px 2px rgba(0,0,0,0.04);">
              <div style="display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 8px;">
                <div style="display: flex; align-items: center; gap: 6px;">
                  <span style="font-size: 16px;">${icon}</span>
                  <span style="font-size: 13px; font-weight: 700; color: #0f172a;">
                    ${roomLabel}
                  </span>
                </div>
                <span style="font-size: 11px; font-weight: 700; padding: 2px 8px; border-radius: 20px; background: ${statusBg}; color: ${statusColor};">
                  ${statusText}
                </span>
              </div>

              <div style="font-size: 13px; color: #334155; font-weight: 600; margin-bottom: 4px;">
                ${req.title}
              </div>

              ${req.notes ? `<div style="font-size: 12px; color: #64748b; font-style: italic; margin-bottom: 6px;">"${req.notes}"</div>` : ''}

              <div style="display: flex; justify-content: space-between; align-items: center; font-size: 11px; color: #94a3b8; border-top: 1px dashed #f1f5f9; padding-top: 6px; margin-top: 6px;">
                <span>🕒 ${req.createdAt ? new Date(req.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : ''}</span>
                <div style="display: flex; align-items: center; gap: 8px;">
                  <span>${assignedLabel}</span>
                  ${
                    req.status === 'pending'
                      ? `
                    <button
                      type="button"
                      style="border: 1px solid #fca5a5; background: #fff1f2; color: #e11d48; font-size: 11px; font-weight: 600; padding: 2px 8px; border-radius: 6px; cursor: pointer;"
                      onclick="window.handleCancelGuestRequest?.('${req.id}')"
                    >
                      ${t('cancelRequestBtn')}
                    </button>
                  `
                      : ''
                  }
                </div>
              </div>
            </div>
          `;
        })
        .join('')}
    </div>
  `;
}

export function saveRoomServiceInputsFromDom(): void {
  const rInput = (document.getElementById('rs-room-number') || document.getElementById('rs-room-number-maint')) as HTMLInputElement;
  const nInput = (document.getElementById('rs-guest-name') || document.getElementById('rs-guest-name-maint')) as HTMLInputElement;
  const pInput = (document.getElementById('rs-guest-phone') || document.getElementById('rs-guest-phone-maint')) as HTMLInputElement;
  const notesInput = document.getElementById('rs-notes') as HTMLTextAreaElement;
  const issueInput = document.getElementById('rs-issue-desc') as HTMLTextAreaElement;

  if (rInput && rInput.value) formRoomNumber = rInput.value;
  if (nInput && nInput.value) formGuestName = nInput.value;
  if (pInput && pInput.value) formGuestPhone = pInput.value;
  if (notesInput) formNotes = notesInput.value;
  if (issueInput) formIssueDesc = issueInput.value;
}

export async function initRoomServicePage(): Promise<void> {
  // Sync state from DOM/localStorage on load
  const savedRoom = localStorage.getItem('_guest_room_number') || '';
  const savedName = localStorage.getItem('_guest_name') || '';
  const savedPhone = localStorage.getItem('_guest_phone') || '';
  if (savedRoom && !formRoomNumber) formRoomNumber = savedRoom;
  if (savedName && !formGuestName) formGuestName = savedName;
  if (savedPhone && !formGuestPhone) formGuestPhone = savedPhone;

  // Function to re-render ONLY the content area without re-mounting the whole page
  const renderContentOnly = () => {
    const contentEl = document.getElementById('room-service-content');
    if (contentEl) {
      const lang = getCurrentLanguage();
      contentEl.innerHTML = renderCurrentTabContent(lang, formRoomNumber, formGuestName, formGuestPhone);
      bindInputListeners();
    }
  };

  const bindInputListeners = () => {
    const rInput = (document.getElementById('rs-room-number') || document.getElementById('rs-room-number-maint')) as HTMLInputElement;
    if (rInput) {
      rInput.value = formRoomNumber;
      rInput.oninput = (e) => {
        formRoomNumber = (e.target as HTMLInputElement).value;
        localStorage.setItem('_guest_room_number', formRoomNumber);
      };
    }

    const nInput = (document.getElementById('rs-guest-name') || document.getElementById('rs-guest-name-maint')) as HTMLInputElement;
    if (nInput) {
      nInput.value = formGuestName;
      nInput.oninput = (e) => {
        formGuestName = (e.target as HTMLInputElement).value;
        localStorage.setItem('_guest_name', formGuestName);
      };
    }

    const pInput = (document.getElementById('rs-guest-phone') || document.getElementById('rs-guest-phone-maint')) as HTMLInputElement;
    if (pInput) {
      pInput.value = formGuestPhone;
      pInput.oninput = (e) => {
        formGuestPhone = (e.target as HTMLInputElement).value;
        localStorage.setItem('_guest_phone', formGuestPhone);
      };
    }

    const notesInput = document.getElementById('rs-notes') as HTMLTextAreaElement;
    if (notesInput) {
      notesInput.value = formNotes;
      notesInput.oninput = (e) => {
        formNotes = (e.target as HTMLTextAreaElement).value;
      };
      notesInput.onfocus = () => {
        notesInput.scrollIntoView({ behavior: 'smooth', block: 'center' });
      };
    }

    const issueInput = document.getElementById('rs-issue-desc') as HTMLTextAreaElement;
    if (issueInput) {
      issueInput.value = formIssueDesc;
      issueInput.oninput = (e) => {
        formIssueDesc = (e.target as HTMLTextAreaElement).value;
      };
      issueInput.onfocus = () => {
        issueInput.scrollIntoView({ behavior: 'smooth', block: 'center' });
      };
    }
  };

  // 1. Fetch Config in background and update
  fetchGuestServicesConfig().then((cfg) => {
    if (cfg && (Array.isArray(cfg.housekeepingItems) || Array.isArray(cfg.maintenanceCategories) || cfg.coverImage !== undefined)) {
      serviceConfig = {
        housekeepingItems: Array.isArray(cfg.housekeepingItems) ? cfg.housekeepingItems : DEFAULT_HOUSEKEEPING_ITEMS,
        maintenanceCategories: Array.isArray(cfg.maintenanceCategories) ? cfg.maintenanceCategories : DEFAULT_MAINTENANCE_CATEGORIES,
        coverImage: typeof cfg.coverImage === 'string' ? cfg.coverImage : '',
      };
      // Re-render hero card with actual cover image
      const heroEl = document.getElementById('room-service-hero-card');
      if (heroEl) {
        heroEl.outerHTML = renderRoomServiceHeroCard(serviceConfig.coverImage, savedRoom);
      }
      // Re-render ONLY content to preserve inputs
      renderContentOnly();
    }
  }).catch(() => {});

  // 2. Load my requests
  if (formRoomNumber) {
    loadRequestsForRoom(formRoomNumber);
  }

  // 3. Register global event handlers
  (window as any).handleRoomServiceInput = (field: 'room' | 'name' | 'phone' | 'notes' | 'issue', val: string) => {
    if (field === 'room') {
      formRoomNumber = val;
      localStorage.setItem('_guest_room_number', val);
      const r1 = document.getElementById('rs-room-number') as HTMLInputElement;
      const r2 = document.getElementById('rs-room-number-maint') as HTMLInputElement;
      if (r1 && r1.value !== val) r1.value = val;
      if (r2 && r2.value !== val) r2.value = val;
    } else if (field === 'name') {
      formGuestName = val;
      localStorage.setItem('_guest_name', val);
      const n1 = document.getElementById('rs-guest-name') as HTMLInputElement;
      const n2 = document.getElementById('rs-guest-name-maint') as HTMLInputElement;
      if (n1 && n1.value !== val) n1.value = val;
      if (n2 && n2.value !== val) n2.value = val;
    } else if (field === 'phone') {
      formGuestPhone = val;
      localStorage.setItem('_guest_phone', val);
      const p1 = document.getElementById('rs-guest-phone') as HTMLInputElement;
      const p2 = document.getElementById('rs-guest-phone-maint') as HTMLInputElement;
      if (p1 && p1.value !== val) p1.value = val;
      if (p2 && p2.value !== val) p2.value = val;
    } else if (field === 'notes') {
      formNotes = val;
    } else if (field === 'issue') {
      formIssueDesc = val;
    }
  };

  (window as any).switchRoomServiceTab = (tab: typeof activeTab) => {
    saveInputsFromDom();
    activeTab = tab;

    // Update tab button classes and styles directly without whole app re-render
    ['hk', 'maint', 'history'].forEach((tKey) => {
      const mapped = tKey === 'hk' ? 'housekeeping' : tKey === 'maint' ? 'maintenance' : 'my-requests';
      const btn = document.getElementById(`tab-btn-${tKey}`);
      if (btn) {
        const isActive = activeTab === mapped;
        const activeColor = mapped === 'housekeeping' ? '#00B900' : mapped === 'maintenance' ? '#2563eb' : '#7c3aed';
        btn.style.background = isActive ? activeColor : '#fff';
        btn.style.color = isActive ? '#fff' : '#64748b';
        btn.style.borderColor = isActive ? activeColor : '#e2e8f0';
      }
    });

    renderContentOnly();
  };

  // Initial binding
  bindInputListeners();

  const saveInputsFromDom = saveRoomServiceInputsFromDom;

  (window as any).adjustItemQty = (itemId: string, delta: number) => {
    saveInputsFromDom();
    const current = selectedItems[itemId] || 0;
    const targetItem = serviceConfig?.housekeepingItems?.find((i) => i.id === itemId);
    const maxQty = typeof targetItem?.maxQuantity === 'number' && targetItem.maxQuantity > 0 ? targetItem.maxQuantity : 4;
    const next = Math.max(0, Math.min(maxQty, current + delta));
    if (next === 0) {
      delete selectedItems[itemId];
    } else {
      selectedItems[itemId] = next;
    }
    renderContentOnly();
  };

  (window as any).selectIssueCategory = (catId: string) => {
    saveInputsFromDom();
    selectedIssueCategory = catId;
    renderContentOnly();
  };

  (window as any).handleCancelGuestRequest = async (taskId: string) => {
    if (!confirm(t('confirmCancelRequest'))) return;
    try {
      const res = await cancelGuestRequest(taskId);
      if (res.success) {
        const room = localStorage.getItem('_guest_room_number') || formRoomNumber;
        if (room) {
          await loadRequestsForRoom(room);
        }
      } else {
        alert(res.error || t('cannotCancelRequest'));
      }
    } catch (err: any) {
      alert(t('errorCancelRequest') + (err.message || ''));
    }
  };

  (window as any).refreshMyRequests = () => {
    const room = localStorage.getItem('_guest_room_number') || '';
    if (room) {
      loadRequestsForRoom(room);
    }
  };

  (window as any).handleSubmitHousekeepingRequest = async () => {
    const roomInput = document.getElementById('rs-room-number') as HTMLInputElement;
    const nameInput = document.getElementById('rs-guest-name') as HTMLInputElement;
    const phoneInput = document.getElementById('rs-guest-phone') as HTMLInputElement;
    const notesInput = document.getElementById('rs-notes') as HTMLTextAreaElement;

    const roomNumber = roomInput?.value.trim() || '';
    if (!roomNumber) {
      alert(t('roomNumberRequired'));
      roomInput?.focus();
      return;
    }

    const itemsList = Object.entries(selectedItems).map(([id, quantity]) => {
      const it = serviceConfig?.housekeepingItems.find((i) => i.id === id);
      const lang = getCurrentLanguage();
      const name = it ? ((it.name as any)[lang] || it.name.en || it.name.th) : id;
      return { id, name, quantity };
    });

    const notes = notesInput?.value.trim() || '';

    if (itemsList.length === 0 && !notes) {
      alert(t('selectAtLeastOneItem'));
      return;
    }

    const guestName = nameInput?.value.trim() || '';
    const guestPhone = phoneInput?.value.trim() || '';

    // Remember in localStorage
    localStorage.setItem('_guest_room_number', roomNumber);
    if (guestName) localStorage.setItem('_guest_name', guestName);
    if (guestPhone) localStorage.setItem('_guest_phone', guestPhone);

    const btn = document.getElementById('rs-submit-btn') as HTMLButtonElement;
    if (btn) {
      btn.disabled = true;
      btn.innerHTML = `<span>⏳</span> ${t('submittingRequest')}`;
    }

    const activeChannel = (typeof sessionStorage !== 'undefined' ? (sessionStorage.getItem('_channel') || localStorage.getItem('_channel')) : null) || 'web';

    const res = await submitGuestRequest({
      category: 'housekeeping',
      roomNumber,
      guestName,
      guestPhone,
      items: itemsList,
      notes,
      channel: activeChannel,
    });

    if (res.success) {
      selectedItems = {};
      formNotes = '';
      (window as any).switchRoomServiceTab?.('my-requests');
      await loadRequestsForRoom(roomNumber);
    } else {
      alert(res.error || t('submitRequestFailed'));
      if (btn) {
        btn.disabled = false;
        btn.innerHTML = `<span>🧹</span> ${t('submitHousekeepingBtn')}`;
      }
    }
  };

  (window as any).handleSubmitMaintenanceRequest = async () => {
    const roomInput = document.getElementById('rs-room-number-maint') as HTMLInputElement;
    const nameInput = document.getElementById('rs-guest-name-maint') as HTMLInputElement;
    const phoneInput = document.getElementById('rs-guest-phone-maint') as HTMLInputElement;
    const descInput = document.getElementById('rs-issue-desc') as HTMLTextAreaElement;

    const roomNumber = roomInput?.value.trim() || '';
    if (!roomNumber) {
      alert(t('roomNumberRequired'));
      roomInput?.focus();
      return;
    }

    const issueNotes = descInput?.value.trim() || '';
    if (!selectedIssueCategory && !issueNotes) {
      alert(t('describeIssueRequired'));
      descInput?.focus();
      return;
    }

    const catObj = serviceConfig?.maintenanceCategories.find((c) => c.id === selectedIssueCategory);
    const lang = getCurrentLanguage();
    const catName = catObj ? ((catObj.name as any)[lang] || catObj.name.en || catObj.name.th) : '';

    const guestName = nameInput?.value.trim() || '';
    const guestPhone = phoneInput?.value.trim() || '';

    localStorage.setItem('_guest_room_number', roomNumber);
    if (guestName) localStorage.setItem('_guest_name', guestName);
    if (guestPhone) localStorage.setItem('_guest_phone', guestPhone);

    const btn = document.getElementById('rs-submit-maint-btn') as HTMLButtonElement;
    if (btn) {
      btn.disabled = true;
      btn.innerHTML = `<span>⏳</span> ${t('submittingMaintenance')}`;
    }

    const activeChannel = (typeof sessionStorage !== 'undefined' ? (sessionStorage.getItem('_channel') || localStorage.getItem('_channel')) : null) || 'web';

    const res = await submitGuestRequest({
      category: 'maintenance',
      roomNumber,
      guestName,
      guestPhone,
      issueCategory: catName,
      notes: issueNotes,
      channel: activeChannel,
    });

    if (res.success) {
      selectedIssueCategory = '';
      formIssueDesc = '';
      (window as any).switchRoomServiceTab?.('my-requests');
      await loadRequestsForRoom(roomNumber);
    } else {
      alert(res.error || t('submitMaintenanceFailed'));
      if (btn) {
        btn.disabled = false;
        btn.innerHTML = `<span>🛠️</span> ${t('submitMaintenanceBtn')}`;
      }
    }
  };

  // Floating "BACK TO TOP" for Room Service
  initBackToTop('roomservice');
}

async function loadRequestsForRoom(roomNumber: string): Promise<void> {
  try {
    const list = await fetchGuestRequests(roomNumber);
    myRequestsList = list;
    // Only re-render content if user is actually on my-requests tab
    if (activeTab === 'my-requests') {
      const contentEl = document.getElementById('room-service-content');
      if (contentEl) {
        const lang = getCurrentLanguage();
        contentEl.innerHTML = renderCurrentTabContent(lang, formRoomNumber, formGuestName, formGuestPhone);
      }
    }
  } catch (err) {
    console.error('Failed to load requests for room:', err);
  }
}
